import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaGithub, FaLinkedin, FaInstagram, FaFacebook } from 'react-icons/fa';
import logo from '../assets/logo.png';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  z-index: 1000;
  padding: 1rem 0;
  transition: all 0.3s ease;
  box-shadow: ${props => props.scrolled ? '0 2px 20px rgba(70, 130, 180, 0.3)' : 'none'};
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  text-decoration: none;
  
  span {
    color: #4682B4;
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.98);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    padding: 2rem;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  color: #ffffff;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  transition: all 0.3s ease;
  position: relative;

  &:hover, &.active {
    color: #4682B4;
    background: rgba(70, 130, 180, 0.1);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: #4682B4;
    transition: width 0.3s ease;
  }

  &:hover::after, &.active::after {
    width: 80%;
  }

  @media (max-width: 768px) {
    color: #4682B4;
    font-size: 1.2rem;
    padding: 1rem 2rem;

    &:hover, &.active {
      color: #ffffff;
      background: rgba(70, 130, 180, 0.2);
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-left: 2rem;

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 2rem;
  }
`;

const SocialLink = styled.a`
  color: #cccccc;
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    color: #4682B4;
    transform: translateY(-3px);
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #ffffff;
  cursor: pointer;
  z-index: 1001;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const navItems = [
    { path: '/', text: 'Home' },
    { path: '/about', text: 'About' },
    { path: '/portfolio', text: 'Portfolio' },
    { path: '/services', text: 'Services' },
    { path: '/skills', text: 'Skills' },
    { path: '/contact', text: 'Contact' }
  ];

  return (
    <Nav scrolled={scrolled}>
      <NavContainer>
        <Logo to="/">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src={logo} alt="Softvance Solution" style={{ height: '45px', width: 'auto' }} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ color: '#ffffff', fontWeight: '700', fontSize: '1.5rem', lineHeight: '1' }}>SOFTVANCE</span>
              <span style={{ color: '#4682B4', fontWeight: '700', fontSize: '1rem', lineHeight: '1' }}>SOLUTION</span>
            </div>
          </div>
        </Logo>

        <MenuToggle onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MenuToggle>

        <NavMenu isOpen={isOpen}>
          {navItems.map((item) => (
            <NavItem key={item.path}>
              <NavLink 
                to={item.path} 
                className={location.pathname === item.path ? 'active' : ''}
              >
                {item.text}
              </NavLink>
            </NavItem>
          ))}
          
          <SocialLinks>
            <SocialLink href="https://github.com" target="_blank" rel="noopener noreferrer">
              <FaGithub style={{ color: '#333333' }} />
            </SocialLink>
            <SocialLink href="https://www.instagram.com/softvancesolutions/" target="_blank" rel="noopener noreferrer">
              <FaInstagram style={{ color: '#E4405F' }} />
            </SocialLink>
            <SocialLink href="https://www.linkedin.com/in/softvancesol/" target="_blank" rel="noopener noreferrer">
              <FaLinkedin style={{ color: '#0077B5' }} />
            </SocialLink>
          </SocialLinks>
        </NavMenu>
      </NavContainer>
    </Nav>
  );
};

export default Navbar;
